import axios from 'axios';
import {getAuthHeaders, getBaseUrl} from '../helpers/rulesApiHelpers';
import {
  DEFAULT_COST_CALCULATOR_FIELDS,
  DEFAULT_COMPUTED_SUMMARY
} from '../utils/costCalculatorUtils';

export const COST_CALCULATOR_FETCH_REQUEST = 'COST_CALCULATOR_FETCH_REQUEST';
export const COST_CALCULATOR_FETCH_SUCCESS = 'COST_CALCULATOR_FETCH_SUCCESS';
export const COST_CALCULATOR_FETCH_FAILURE = 'COST_CALCULATOR_FETCH_FAILURE';
export const COST_CALCULATOR_SUBMIT_REQUEST = 'COST_CALCULATOR_SUBMIT_REQUEST';
export const COST_CALCULATOR_SUBMIT_SUCCESS = 'COST_CALCULATOR_SUBMIT_SUCCESS';
export const COST_CALCULATOR_SUBMIT_FAILURE = 'COST_CALCULATOR_SUBMIT_FAILURE';

// ─── Fetch current saved state + defaults ──────────────────────────────────────
// GET {api_url}/cost_calculator.json — public endpoint, no backend changes needed
// (see the port spec: CostCalculatorsController has no auth before_action beyond the
// app-wide ones, none of which gate this action).
//
// `apiUrlOverride`: when the screen is reached via the PUBLIC, unauthenticated route
// (see src/utils/publicRoutes.js), there is no logged-in tenant to read a base URL or
// access token from AsyncStorage — pass PUBLIC_COST_CALCULATOR_API_URL explicitly instead,
// and skip the Authorization header entirely (the endpoint doesn't check it either way).
export function fetchCostCalculator(apiUrlOverride) {
  return async (dispatch) => {
    dispatch({type: COST_CALCULATOR_FETCH_REQUEST});
    try {
      const url = apiUrlOverride || (await getBaseUrl());
      const headers = apiUrlOverride ? {} : await getAuthHeaders();
      const response = await axios.get(`${url}/cost_calculator.json`, {headers});
      dispatch({type: COST_CALCULATOR_FETCH_SUCCESS, payload: response.data});
      return {success: true, data: response.data};
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to load the cost calculator';
      dispatch({type: COST_CALCULATOR_FETCH_FAILURE, payload: {message}});
      return {success: false, error: message};
    }
  };
}

/**
 * Submit the calculator — used for both the explicit "Save" affordance and the
 * "Tell your team" Send action, matching the legacy tool's single endpoint:
 * GET {api_url}/email_calculations.json?<all fields as query params>
 *
 * - `onlySave: true` → just persists the shared GeneralSetting.cost_calculator_url
 *   singleton, no email/CampaignMonitor call (server-side, see spec).
 * - `onlySave: false` → sends CostCalculatorMailer.send_cost_calculation to up to 3
 *   recipients (server always CCs groovepacker+calculator@gmail.com — do not add a
 *   client-side equivalent, it's server-only), and if `followUpEmail` is true, posts
 *   the lead to CampaignMonitor. Entirely server-side; this thunk just has to pass
 *   `follow_up_email=true` and `recipient_one=<email>`.
 *
 * `fields` is the full primitive field set; `computed` is the current derived summary
 * (total_cost, monthly_shipping, etc.) — both get sent, matching the legacy tool's
 * `$("#cost_calc").serialize()` which serializes the whole form including computed
 * hidden/readonly inputs.
 */
export function submitCostCalculation({fields, computed, recipients, recipientName, emailText, costHeader, followUpEmail, onlySave, apiUrlOverride}) {
  return async (dispatch) => {
    dispatch({type: COST_CALCULATOR_SUBMIT_REQUEST});
    try {
      const url = apiUrlOverride || (await getBaseUrl());
      const headers = apiUrlOverride ? {} : await getAuthHeaders();

      const params = {
        ...fields,
        ...computed,
        recipient_one: recipients?.[0] || '',
        recipient_two: recipients?.[1] || '',
        recipient_three: recipients?.[2] || '',
        recipient_name: recipientName || '',
        email_text: emailText || '',
        cost_header: costHeader || '',
        follow_up_email: followUpEmail ? 'true' : 'false',
        only_save: onlySave ? 'true' : 'false'
      };

      const response = await axios.get(`${url}/email_calculations.json`, {headers, params});
      dispatch({type: COST_CALCULATOR_SUBMIT_SUCCESS, payload: response.data});
      return {success: true, data: response.data};
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to send the cost calculation';
      dispatch({type: COST_CALCULATOR_SUBMIT_FAILURE, payload: {message}});
      return {success: false, error: message};
    }
  };
}

export {DEFAULT_COST_CALCULATOR_FIELDS, DEFAULT_COMPUTED_SUMMARY};
